'use strict';

/**
 * Pict-Provider-DeployAPI
 * -----------------------
 * Browser client for the graphical deploy surface. One read (GET /1.0/Releases) backs the Releases
 * page: what commit runs on each environment, the As-Builts waiting to validate or promote, and the
 * in-flight deploy requests. requestDeploy() queues a DeployRequest (POST /1.0/DeployRequest) that a
 * deploy node claims and runs; the page never deploys anything itself, so no deploy credentials live
 * in the browser. See the graphical-deploy plan.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'DeployAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderDeployAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Releases)
		{
			this.fable.AppData.Releases =
			{
				Loaded: false, LoadError: null,
				Environments: [], AsBuilts: [], DeployRequests: [], OpenRuns: [], Nodes: [], DeployFleet: null
			};
		}
	}

	loadReleases()
	{
		this.initializeAppData();
		let tmpStore = this.fable.AppData.Releases;
		return libFrameworkClient.getJSON(this.fable, this.routePrefix + 'Releases')
			.then((pBody) =>
			{
				tmpStore.Environments = (pBody && pBody.Environments) || [];
				tmpStore.AsBuilts = (pBody && pBody.AsBuilts) || [];
				tmpStore.DeployRequests = (pBody && pBody.DeployRequests) || [];
				// The runs holding the deploy lanes. Deploying and promoting are runs now, so a lane can be
				// busy with nothing at all in the request feed; this store whitelists, so forgetting this
				// field reads on screen as a free lane and puts the stale Promote button back.
				tmpStore.OpenRuns = (pBody && pBody.OpenRuns) || [];
				// Node liveness. This store whitelists, so a field the server sends and this forgets is a
				// field that reads on screen as nothing at all -- the same way CanApprove once read as a
				// missing button. Kept as its own line for that reason.
				tmpStore.Nodes = (pBody && pBody.Nodes) || [];
				tmpStore.DeployFleet = (pBody && pBody.DeployFleet) || null;
				// Whether this person may clear a stuck record. The page hides the recovery controls without
				// it, so a field the server sends and this store forgets reads on screen as a missing button.
				tmpStore.CanApprove = !!(pBody && pBody.CanApprove);
				tmpStore.Loaded = true;
				tmpStore.LoadError = null;
				return pBody;
			})
			.catch((pError) => { tmpStore.LoadError = pError.message; tmpStore.Loaded = true; return null; });
	}

	// Queue a deploy for a deploy node to claim and run. pAction is 'DeployDev' or 'Promote'; the environment
	// follows from the action. pCommitRef is optional -- empty means "the current trunk", resolved at run time.
	//
	// The server refuses this with a 409 while that environment already has a deploy in flight. The rejected
	// error carries the server's sentence and StatusCode, so a caller can tell "the lane is busy" from a real
	// failure and say so in those words rather than as an error.
	requestDeploy(pAction, pCommitRef)
	{
		let tmpAction = (pAction === 'Promote') ? 'Promote' : 'DeployDev';
		let tmpEnvironment = (tmpAction === 'Promote') ? 'prod' : 'dev';
		return libFrameworkClient.postJSON(this.fable, this.routePrefix + 'DeployRequest',
			{ Action: tmpAction, Environment: tmpEnvironment, CommitRef: pCommitRef || '', Status: 'Requested' });
	}

	// Queue one past a deploy that is already in flight. A different route from requestDeploy on purpose: this
	// one needs content.approve and a written reason, and it records which request it jumped. The plain create
	// stays overridable by nobody, which is what keeps a node from forcing its own way past a busy lane.
	forceDeploy(pAction, pReason, pCommitRef)
	{
		let tmpAction = (pAction === 'Promote') ? 'Promote' : 'DeployDev';
		let tmpEnvironment = (tmpAction === 'Promote') ? 'prod' : 'dev';
		return libFrameworkClient.postJSON(this.fable, this.routePrefix + 'DeployRequest/Force',
			{ Action: tmpAction, Environment: tmpEnvironment, CommitRef: pCommitRef || '', Reason: pReason || '' });
	}
}

module.exports = PictProviderDeployAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
